/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import type { Logger } from '@quickplay/foundation';

/** Parses supplied numeric metadata without applying wire rounding or defaults. */
export function parseGodavariNumber(value: unknown): number | undefined {
  let number = Number.NaN;
  if (typeof value === 'number') {
    number = value;
  } else if (typeof value === 'string' && value.trim() !== '') {
    number = Number(value);
  }
  return Number.isFinite(number) ? number : undefined;
}

function formatGodavariNumber(key: string, value: unknown, fallback: number, logger?: Logger): number {
  if (value === undefined) {
    return fallback;
  }
  const number = parseGodavariNumber(value);
  if (number === undefined) {
    logger?.warn(`Invalid Godavari numeric field ${key}; using ${fallback}.`);
    return fallback;
  }
  if (key === 'event_current_playback_rate') {
    return number;
  }
  if (key === 'ads_length_duration') {
    return Math.ceil(number);
  }
  return Math.trunc(number);
}

/** Applies the vendor's numeric wire format after metadata has been merged. */
export function formatGodavariNumbers<T extends object, D extends Record<string, number>>(
  payload: T,
  defaults: D,
  logger?: Logger,
): Omit<T, keyof D> & D {
  const result = { ...payload, ...defaults };
  const isRecord = typeof payload === 'object' && payload !== null && !Array.isArray(payload);
  const input: Record<string, unknown> = isRecord ? Object.fromEntries(Object.entries(payload)) : {};
  if (!isRecord && payload !== undefined) {
    logger?.warn('Invalid Godavari object metadata; ignoring its fields.');
  }
  for (const [key, defaultValue] of Object.entries(defaults)) {
    Object.assign(result, { [key]: formatGodavariNumber(key, input[key], defaultValue, logger) });
  }
  return result;
}
